import { ListDataTypeEnum, ListValue } from 'views/backoffice/CustomLists/types';

/**
 * For User list type: include option if userValue.enabled is true,
 * or if userValue.enabled is false only when option.id === selectedId (keep selected value visible).
 */
export const filterUserEnabledOptions = (
    opts: ListValue[],
    listDatatype: ListDataTypeEnum | undefined,
    selectedId?: string
): ListValue[] => {
    if (listDatatype !== 'User') return opts;
    return opts.filter(
        (opt) =>
            opt.userValue?.enabled === true || (opt.userValue?.enabled === false && opt.id === selectedId)
    );
};

/**
 * Target number of visible options to accumulate before we stop auto-fetching
 * paginated pages and hand control back to the scroll-triggered "load more".
 * Roughly a listbox-worth of rows so the list is always tall enough to scroll.
 */
export const MIN_VISIBLE_OPTIONS = 10;

/**
 * Whether the paginated query has reached the last backend page.
 * `total` is the unfiltered total reported by the backend; `page` is 1-based.
 */
export const hasReachedLastPage = (page: number, limit: number, total: number): boolean => page * limit >= total;

/**
 * Whether to fetch the next page automatically, WITHOUT waiting for the user to scroll.
 *
 * User-enabled filtering (OBD-870) shrinks a raw page of `limit` rows down to
 * fewer visible options, which stalls the scroll-triggered "load more" when:
 *  - the visible list is too short to be scrollable (scroll never fires), or
 *  - a whole page filters down to zero (list height never grows).
 * Only call this once the last page has NOT been reached.
 *
 * `rawPageSize` guards the tail: an empty backend page (0 rows) means there is
 * genuinely no more data, so we stop even if the target was not reached.
 */
export const shouldAutoAdvancePage = ({
    visibleCount,
    lastPageAdded,
    rawPageSize,
    minVisible = MIN_VISIBLE_OPTIONS
}: {
    visibleCount: number;
    lastPageAdded: number;
    rawPageSize: number;
    minVisible?: number;
}): boolean => {
    if (rawPageSize === 0) return false;
    return visibleCount < minVisible || lastPageAdded === 0;
};

export const getFilteredValues = (valuesToSearchIn: ListValue[], listDatatype: ListDataTypeEnum | undefined, valueToSearchFor: string) =>
    valuesToSearchIn.filter(
        (opt) =>
            (listDatatype === 'Value' && opt.value.toLowerCase().includes(valueToSearchFor.toLowerCase())) ||
            (listDatatype === 'User' &&
                `${opt.userValue?.firstName?.toLowerCase()} ${opt.userValue?.lastName.toLowerCase()}`.includes(
                    valueToSearchFor.toLowerCase()
                )) ||
            (listDatatype === 'Object' &&
                opt.dynamicObjectValue?.objectValues
                    ?.reduce((acc, cur) => `${acc} ${cur}`, '')
                    ?.toLowerCase()
                    .includes(valueToSearchFor.toLowerCase())) ||
            (!listDatatype && opt?.name && opt.name.toLowerCase().includes(valueToSearchFor.toLowerCase()))
    );
